import { requireService } from '@repo/di-ez';
import { GRID_ROW_NUMBER_COL_DEF, TableInput, TableNumberInput, TableSelect } from '@repo/go-table';
import type { GridColDef, GridRenderCellParams, GridRenderEditCellParams, TGoTableColDef } from '@repo/go-table';
import { formatGeographicCoordinate, hasValue } from '@repo/utils';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { DEFAULT_NUMBER_DECIMAL_PLACES } from '../../../consts';
import { getTrajStationTypeTKey, TRAJ_STATION_TYPES } from '../../../consts/traj-station-type';
import type { FactTrajectoryPointEntity } from '../../../service/entities';
import type { TFactTrajectoryPoint } from '../../../service/model';
import { applyPointFieldUpdate } from '../apply-point-field-update';
import { TrajectoryTableCell } from '../components/trajectory-table-cell';
import tableCellStyles from '../components/trajectory-table-cell.module.scss';
import {
  getTrajectoryColumnDefinition,
  TRAJECTORY_COLUMN_DEFINITIONS,
  type TrajectoryColumnField,
  type TrajectoryColumnKind,
} from '../trajectory-column-definitions';
import { formatTableNumber } from '../utils/format-table-number';
import { getTrajectoryColumnHeaderTKey } from '../utils/get-column-header-t-key';

type TSelectOption = { value: string; label: string };

const EMPTY_DISPLAY = '—';

const GRID_COLUMN_TYPE_BY_KIND: Record<TrajectoryColumnKind, GridColDef['type']> = {
  number: 'number',
  coordinate: 'number',
  text: 'string',
  select: 'singleSelect',
  geo: 'string',
};

const formatSelectValue = (
  field: TrajectoryColumnField,
  value: unknown,
  selectOptionsByField: Partial<Record<TrajectoryColumnField, readonly TSelectOption[]>>,
): string => {
  if (!hasValue(value)) {
    return EMPTY_DISPLAY;
  }

  const optionLabel = selectOptionsByField[field]?.find((option) => option.value === value)?.label;

  return optionLabel ?? String(value);
};

export const useTrajectoryColumns = (
  useShortColumnHeaders = true,
  isEditing = false,
  numberDecimalPlaces = DEFAULT_NUMBER_DECIMAL_PLACES,
): GridColDef[] => {
  const { t, i18n } = useTranslation();
  const trajectoryService = requireService('trajectoryService');
  const validationStore = requireService('trajectoryTableValidationStore');

  const typeTrajStationOptions = useMemo(
    () =>
      TRAJ_STATION_TYPES.map((value) => ({
        value,
        label: t(getTrajStationTypeTKey(value)),
      })),
    [t],
  );

  const selectOptionsByField = useMemo(
    (): Partial<Record<TrajectoryColumnField, readonly TSelectOption[]>> => ({
      typeTrajStation: typeTrajStationOptions,
    }),
    [typeTrajStationOptions],
  );

  return useMemo((): GridColDef[] => {
    const baseColumn: Partial<TGoTableColDef<FactTrajectoryPointEntity>> = {
      headerAlign: 'center',
      verticalHeaderAlign: 'bottom',
      minWidth: 64,
      resizable: true,
      disableColumnMenu: isEditing,
      disableReorder: isEditing,
      sortable: !isEditing,
      valueGetter: (_, row, column) => {
        return trajectoryService.getFieldValue(row?.entityId, column.field as keyof TFactTrajectoryPoint);
      },
      valueFormatter: (value, _row, column) => {
        const field = column.field as TrajectoryColumnField;
        const definition = getTrajectoryColumnDefinition(field);

        switch (definition.kind) {
          case 'geo':
            return formatGeographicCoordinate(value as number | null | undefined, field as 'latitude' | 'longitude');
          case 'select':
            return formatSelectValue(field, value, selectOptionsByField);
          case 'text':
            return hasValue(value) ? String(value) : EMPTY_DISPLAY;
          case 'coordinate':
          case 'number':
            return formatTableNumber(value, numberDecimalPlaces, i18n.language);
        }
      },
      renderCell: (params: GridRenderCellParams<FactTrajectoryPointEntity>) => <TrajectoryTableCell params={params} />,
      renderEditCell: (params: GridRenderEditCellParams<FactTrajectoryPointEntity>) => {
        const entityId = params.row.entityId;
        const field = params.field as TrajectoryColumnField;

        switch (params.colDef.type) {
          case 'number':
            return (
              <TrajectoryTableCell params={params}>
                <TableNumberInput
                  cellParams={params}
                  allowClear={false}
                  autoFocus
                  className={tableCellStyles.rightAlignedNumberInput}
                  value={params.value as number | null}
                  onChange={(value) => {
                    if (typeof value !== 'number' && hasValue(value)) {
                      return;
                    }

                    applyPointFieldUpdate(trajectoryService, validationStore, entityId, {
                      [field]: value ?? null,
                    });
                  }}
                />
              </TrajectoryTableCell>
            );
          case 'string':
            return (
              <TrajectoryTableCell params={params}>
                <TableInput
                  cellParams={params}
                  value={params.value as string | null}
                  onChange={({ target: { value } }) => {
                    applyPointFieldUpdate(trajectoryService, validationStore, entityId, {
                      [field]: value || null,
                    });
                  }}
                />
              </TrajectoryTableCell>
            );
          case 'singleSelect': {
            const options = selectOptionsByField[field] ?? [];

            return (
              <TrajectoryTableCell params={params}>
                <TableSelect
                  autoFocus
                  defaultOpen
                  cellParams={params}
                  value={params.value as string | null}
                  options={[...options]}
                  onChange={(value) => {
                    if (typeof value !== 'string') {
                      return;
                    }

                    applyPointFieldUpdate(trajectoryService, validationStore, entityId, {
                      [field]: value,
                    });
                  }}
                />
              </TrajectoryTableCell>
            );
          }
          default:
            return undefined;
        }
      },
    };

    const dataColumns: GridColDef<FactTrajectoryPointEntity>[] = TRAJECTORY_COLUMN_DEFINITIONS.map((definition) => {
      const { field, kind, editor, minWidth } = definition;

      return {
        ...baseColumn,
        field,
        type: GRID_COLUMN_TYPE_BY_KIND[kind],
        minWidth: minWidth ?? baseColumn.minWidth,
        editable: isEditing && !!editor,
        headerName: t(getTrajectoryColumnHeaderTKey(field, useShortColumnHeaders)),
        valueOptions: editor === 'select' ? selectOptionsByField[field] : undefined,
        align: 'right' as const,
      };
    });

    return [
      {
        ...GRID_ROW_NUMBER_COL_DEF,
      },
      ...dataColumns,
    ];
  }, [
    i18n.language,
    isEditing,
    numberDecimalPlaces,
    selectOptionsByField,
    t,
    trajectoryService,
    useShortColumnHeaders,
    validationStore,
  ]);
};
